import Immutable from 'immutable';

import { translate } from 'sharedreact/utils/LanguageSupport';

function _translateKey (key: string): string {
  return translate('shared.filter.label.' + key);
}

const FilterRuleOperator = {
  CONTAINS: 'CONTAINS',
  IS: 'IS',
  ISNOT: 'ISNOT',
  ISEMPTY: 'ISEMPTY',
  AND: 'AND',
  OR: 'OR',
  FROM: 'FROM',
  UNTIL: 'UNTIL',
  STARTS_WITH: 'STARTS_WITH',
  ENDS_WITH: 'END_WITH',
  EXISTS: 'EXISTS'
};

const Content: {
  key: string | null,
  value: string | null,
  operator: string
} = {
  key: null,
  value: '',
  operator: ''
};

const _knownKeys: string[] = [
  'name',
  'productname',
  'cusref',
  'productionjobname',
  'ordermaterial',
  'productid',
  'creationdate',
  'description',
  'duedate',
  'actordate',
  'customer',
  'customername',
  'customerid',
  'city',
  'country',
  'contact',
  'diename',
  'dietype',
  'ordermaterialname',
  'state',
  'inkset',
  'type',
  'press',
  'thickness',
  'weight',
  'marksetname',
  'modificationdate',
  'width',
  'height',
  'pressname',
  'dfeserver',
  'mediatypedetails',
  'username',
  'fullname',
  'role',
  'email',
  'domain',
  'owner',
  'subject',
  'productiontype',
  'kind',
  'createdin',
  'starteddate',
  'jobname',
  'workflowname',
  'orderid',
  'tags',
  'inactivateddate',
  'inactivatedby',
  'sharedfrom',
  'location'
];

function getTranslatedKeys (supportedKeys: Immutable.List<string> | string[]): Immutable.List<string> | string[] {
  const translateKey = (knownKey: string): string => _translateKey(knownKey).toLowerCase().replace(/\s/g, '');
  return supportedKeys.map(translateKey);
}

function _addQuotesIfNeeded (value: string | number): string | number {
  if (typeof value === 'string') {
    if (/[\s:;]/g.test(value)) {
      return '"' + value + '"';
    }
  }

  return value;
}

function _operatorToStringPrefix (operator: string): string {
  if (operator === FilterRuleOperator.CONTAINS) {
    return '';
  } else if (operator === FilterRuleOperator.IS) {
    return '=';
  } else if (operator === FilterRuleOperator.ENDS_WITH) {
    return '*';
  }
  return '';
}

function _operatorToStringPostfix (operator: string): string {
  if (operator === FilterRuleOperator.STARTS_WITH) {
    return '*';
  }
  return '';
}

class FilterRule extends Immutable.Record(Content) {
  static CONTAINS: string;
  static IS: string;
  static ISNOT: string;
  static ISEMPTY: string;
  static FROM: string;
  static UNTIL: string;
  static STARTS_WITH: string;
  static ENDS_WITH: string;
  static EXISTS: string;

  toString (): string {
    if (this.key) {
      return (
        _addQuotesIfNeeded(FilterRule.keyToString(this.key)) +
        ':' +
        _operatorToStringPrefix(this.operator) +
        _addQuotesIfNeeded(this.value ?? '') +
        _operatorToStringPostfix(this.operator)
      );
    }

    return (
      _operatorToStringPrefix(this.operator) +
      _addQuotesIfNeeded(this.value ?? '') +
      _operatorToStringPostfix(this.operator)
    );
  }

  static createRule (key: string | null, value: string | null, operator?: string): FilterRule {
    return new FilterRule({key: key, value: value, operator: operator || FilterRuleOperator.CONTAINS});
  }

  static createAND (): FilterRule {
    return new FilterRule({key: null, value: '', operator: FilterRuleOperator.AND});
  }

  static createOR (): FilterRule {
    return new FilterRule({key: null, value: '', operator: FilterRuleOperator.OR});
  }

  static isAND (rule: FilterRule): boolean {
    return (rule.operator === FilterRuleOperator.AND);
  }

  static isOR (rule: FilterRule): boolean {
    return (rule.operator === FilterRuleOperator.OR);
  }

  static isSupportedKey (key: string, supportedKeys: Immutable.List<string> | string[]): boolean {
    return supportedKeys.includes(key) || getTranslatedKeys(supportedKeys).includes(key);
  }

  static stringToKey (humanReadableString: string): string {
    let key = humanReadableString.trim().toLowerCase().replace(/\s/g, '');

    if (!_knownKeys.includes(key)) {
      const keyIndex: number = getTranslatedKeys(_knownKeys).indexOf(key);

      if (keyIndex !== -1) {
        key = _knownKeys[keyIndex];
      }
    }

    return key;
  }

  static keyToString (key: string): string {
    key = key.toLowerCase();
    if (_knownKeys.includes(key)) {
      return _translateKey(key);
    }
    return key;
  }
}

FilterRule.CONTAINS = FilterRuleOperator.CONTAINS;
FilterRule.IS = FilterRuleOperator.IS;
FilterRule.ISNOT = FilterRuleOperator.ISNOT;
FilterRule.ISEMPTY = FilterRuleOperator.ISEMPTY;
FilterRule.FROM = FilterRuleOperator.FROM;
FilterRule.UNTIL = FilterRuleOperator.UNTIL;
FilterRule.STARTS_WITH = FilterRuleOperator.STARTS_WITH;
FilterRule.ENDS_WITH = FilterRuleOperator.ENDS_WITH;
FilterRule.EXISTS = FilterRuleOperator.EXISTS;

export default FilterRule;
